import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getBoolean,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { QuestionDetails, QuestionDetailsDTO } from "../types/questionDetails";
import { SurveyDetailsAndQuestions, SurveyDetailsAndQuestionsDTO } from "../types/surveyDetailsAndQuestions";
/* eslint-disable import/export */
export abstract class GetQuestionDetailsRPC implements
  LeoRPC<GetQuestionDetailsRPC.Request, GetQuestionDetailsRPC.Response, GetQuestionDetailsRPC.Errors.Errors>
{
  abstract execute(
    request: GetQuestionDetailsRPC.Request
  ): Promise<LeoRPCResult<GetQuestionDetailsRPC.Response, GetQuestionDetailsRPC.Errors.Errors>>;
}

export namespace GetQuestionDetailsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    questionId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetQuestionDetailsRPCRequest: boolean = true;
  
    readonly questionId: LeoUUID;
  
  
    constructor(
    questionId: LeoUUID
    ) {
      this.questionId = questionId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const questionId = getLeoUuid(dto, "questionId", InvalidRequestQuestionIdError);
      return new Request(
        questionId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          questionId: this.questionId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetQuestionDetailsRPC(questionId='${this.questionId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    questionDetails: QuestionDetailsDTO;
    surveyDetailsAndQuestions: SurveyDetailsAndQuestionsDTO;
    isResponseResetAllowed: boolean;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetQuestionDetailsResponse: boolean = true;
  
    readonly questionDetails: QuestionDetails;
    readonly surveyDetailsAndQuestions: SurveyDetailsAndQuestions;
    readonly isResponseResetAllowed: boolean;
  
  
    constructor(
    questionDetails: QuestionDetails, 
    surveyDetailsAndQuestions: SurveyDetailsAndQuestions, 
    isResponseResetAllowed: boolean
    ) {
      this.questionDetails = questionDetails;
      this.surveyDetailsAndQuestions = surveyDetailsAndQuestions;
      this.isResponseResetAllowed = isResponseResetAllowed;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const questionDetails = QuestionDetails.fromDTO(getObject(dto, "questionDetails", InvalidResponseQuestionDetailsError));
      const surveyDetailsAndQuestions = SurveyDetailsAndQuestions.fromDTO(getObject(dto, "surveyDetailsAndQuestions", InvalidResponseSurveyDetailsAndQuestionsError));
      const isResponseResetAllowed = getBoolean(dto, "isResponseResetAllowed", InvalidResponseIsResponseResetAllowedError);
      return new Response(
        questionDetails,
        surveyDetailsAndQuestions,
        isResponseResetAllowed
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          questionDetails: this.questionDetails.toDTO(),
          surveyDetailsAndQuestions: this.surveyDetailsAndQuestions.toDTO(),
          isResponseResetAllowed: this.isResponseResetAllowed
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetQuestionDetails(questionDetails='${this.questionDetails}',surveyDetailsAndQuestions='${this.surveyDetailsAndQuestions}',isResponseResetAllowed='${this.isResponseResetAllowed}')`;
  };
  

  export enum RPCError {
    InvalidQuestionId = "INVALID_QUESTION_ID",
    QuestionWasDeleted = "QUESTION_WAS_DELETED",
    JsonVersionNotSupported = "JSON_VERSION_NOT_SUPPORTED"
  }

  export namespace Errors {
    
    export interface InvalidQuestionIdDTO {
      code: string;
    }
    
    export class InvalidQuestionId extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionId;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionIdDTO {
        const dto: InvalidQuestionIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionId.prototype.toString = function toString(): string {
      return `GetQuestionDetailsRPC.INVALID_QUESTION_ID()`;
    };
    
    
    export interface QuestionWasDeletedDTO {
      code: string;
    }
    
    export class QuestionWasDeleted extends LeoRPCError<RPCError> {
      readonly isQuestionWasDeleted: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.QuestionWasDeleted;
        super(code);
      }
    
    
      toDTO(): QuestionWasDeletedDTO {
        const dto: QuestionWasDeletedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    QuestionWasDeleted.prototype.toString = function toString(): string {
      return `GetQuestionDetailsRPC.QUESTION_WAS_DELETED()`;
    };
    
    
    export interface JsonVersionNotSupportedDTO {
      code: string;
    }
    
    export class JsonVersionNotSupported extends LeoRPCError<RPCError> {
      readonly isJsonVersionNotSupported: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.JsonVersionNotSupported;
        super(code);
      }
    
    
      toDTO(): JsonVersionNotSupportedDTO {
        const dto: JsonVersionNotSupportedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    JsonVersionNotSupported.prototype.toString = function toString(): string {
      return `GetQuestionDetailsRPC.JSON_VERSION_NOT_SUPPORTED()`;
    };
    

    export type Errors = InvalidQuestionId | QuestionWasDeleted | JsonVersionNotSupported;
    export type ErrorsDTO = InvalidQuestionIdDTO | QuestionWasDeletedDTO | JsonVersionNotSupportedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidQuestionId:
          return new InvalidQuestionId();
        case RPCError.QuestionWasDeleted:
          return new QuestionWasDeleted();
        case RPCError.JsonVersionNotSupported:
          return new JsonVersionNotSupported();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestQuestionIdError extends GetQuestionDetailsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionIdError";
  }
}

export class InvalidResponseQuestionDetailsError extends GetQuestionDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestionDetailsError";
  }
}

export class InvalidResponseSurveyDetailsAndQuestionsError extends GetQuestionDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyDetailsAndQuestionsError";
  }
}

export class InvalidResponseIsResponseResetAllowedError extends GetQuestionDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseIsResponseResetAllowedError";
  }
}

export class InvalidResponseInvalidQuestionIdError extends GetQuestionDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionIdError";
  }
}

export class InvalidResponseQuestionWasDeletedError extends GetQuestionDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestionWasDeletedError";
  }
}

export class InvalidResponseJsonVersionNotSupportedError extends GetQuestionDetailsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseJsonVersionNotSupportedError";
  }
}

